<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import type { Question } from '~/types/Feedback';
import { getReplyQuestionIdFromHash } from '~/utils/feedbackUrl';

const props = defineProps<{ question: Question }>();

const { userDetails } = storeToRefs(useProfileStore());
const feedbackStore = useFeedbackStore();

const isLikedByMe = computed(() =>
  props.question.rating.find((likedByUserId) => likedByUserId === userDetails.value?.user_id),
);

const isLoading = ref(false);

async function switchLike() {
  isLoading.value = true;
  await feedbackStore.likeQuestion(props.question.id);
  isLoading.value = false;
}

const route = useRoute();
const { isLogged } = storeToRefs(useAuthStore());
const isShowAllReplies = ref(false);

watch(
  () => [route.hash, route.query.questionId],
  () => {
    const focusedQuestionId = Number(route.query.questionId) || getReplyQuestionIdFromHash(route.hash);
    if (focusedQuestionId === props.question.id) isShowAllReplies.value = true;
  },
  { immediate: true },
);

onMounted(async () => {
  if (isLogged.value && Number(route.query.questionIdToLike) === props.question.id) {
    await switchLike();
  }
});

async function handleLikeBtnClick() {
  if (isLogged.value) {
    await switchLike();
  } else {
    await navigateTo({
      path: route.path,
      hash: `#question-${props.question.id}`,
      query: {
        ...route.query,
        questionIdToLike: props.question.id,
      },
    });

    localStorage.setItem('return-url', route.fullPath);
    const toast = useToast();
    toast.info({ component: ToastComponent, props: { text: 'Чтобы поставить лайк, нужно войти' } }, { timeout: 4000 });
    await navigateTo('/login', { external: true });
  }
}

const isShowReplyForm = ref(false);
const isShowReplyModal = ref(false);
const { isMobile } = useBreakpoints();

function handleReplyBtnClick() {
  if (isMobile.value) {
    isShowReplyModal.value = true;
  } else {
    isShowReplyForm.value = !isShowReplyForm.value;
  }
}

async function handleReplySubmit() {
  isShowReplyForm.value = false;
  isShowReplyModal.value = false;

  await nextTick();

  isShowAllReplies.value = true;
}

async function switchShowAllReplies() {
  if (isShowAllReplies.value) {
    const route = useRoute();

    await navigateTo({
      path: route.path,
      hash: `#question-${props.question.id}`,
      query: route.query,
    });
  }

  isShowAllReplies.value = !isShowAllReplies.value;
}
</script>

<template>
  <div
    :id="`question-${question.id}`"
    class="product-question scroll-m-16 lg:scroll-m-52"
  >
    <ProductPageFeedbackUserDetailsBlock
      :avatar="question.user.avatar"
      :created-at="question.created_at"
      :name="question.user.name"
    />

    <div class="product-question__content">
      <div class="break-words">{{ question.message }}</div>

      <ProductPageFeedbackListItemImagesBlock :images="question.images" />

      <div class="flex justify-between items-center">
        <TheBtn
          size="sm"
          @click="handleReplyBtnClick"
        >
          Ответить
        </TheBtn>

        <TheBtn
          :color="isLikedByMe ? 'primary' : 'outline'"
          :is-loading="isLoading"
          prepend-icon="ThumbsUp"
          size="sm"
          @click="handleLikeBtnClick"
        >
          {{ question.rating.length > 0 ? question.rating.length : '' }}
        </TheBtn>
      </div>

      <ClientOnly>
        <ProductPageAddReplyModal
          v-model="isShowReplyModal"
          :question
          @submit-reply="handleReplySubmit"
        />

        <FoldableBlock v-model="isShowReplyForm">
          <div class="pl-8">
            <div class="product-question__reply-divider" />

            <div class="flex gap-4 pt-4">
              <div class="flex justify-center items-center flex-shrink-0 bg-black-10 rounded-full size-12">
                <img
                  v-if="userDetails?.avatar"
                  :src="userDetails.avatar.thumb_url ?? userDetails.avatar.url"
                  alt="user avatar"
                  class="rounded-full"
                />

                <span v-else-if="userDetails?.first_name">
                  {{ userDetails.first_name.charAt(0) }}
                </span>

                <SvgoProfile
                  v-else
                  :font-controlled="false"
                  class="size-7"
                  filled
                />
              </div>

              <ProductPageAddReplyForm
                :question-id="question.id"
                @shrink-form="isShowReplyForm = false"
                @submit-reply="handleReplySubmit"
              />
            </div>
          </div>
        </FoldableBlock>
      </ClientOnly>

      <div v-if="question.replies.length">
        <div class="product-question__reply-divider" />

        <ProductPageReplyListItem
          :question-id="question.id"
          :reply="question.replies[0]"
        />

        <FoldableBlock v-model="isShowAllReplies">
          <div
            v-for="reply in question.replies.slice(1)"
            :key="reply.id"
          >
            <div class="product-question__reply-divider" />

            <ProductPageReplyListItem
              :question-id="question.id"
              :reply
            />
          </div>
        </FoldableBlock>

        <button
          v-if="question.replies.length > 1"
          class="text-black-40 hover:text-black-80 transition"
          @click="switchShowAllReplies"
        >
          {{ isShowAllReplies ? 'Скрыть' : 'Показать все' }} ответы
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.product-question {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 32px 0;

  @include screen-sm {
    gap: 16px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  &__reply-divider {
    width: 100%;
    height: 1px;
    background-color: $black-20;
  }
}
</style>
